import { getWindows, outdoorMask, type TiledMap } from '@veriverse/map-format';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { shadeRuns } from './lighting-math.js';
import { NO_WEATHER, Precipitation, type Area, type WeatherEffects } from './weather-effects.js';

/** Depths: window drops just over the window sky, outdoor drops over decor_above but under the
 * night shade (so rain at night is dark too), lightning over everything. */
const WINDOW_DROP_DEPTH = 1.51;
const OUTDOOR_DROP_DEPTH = 1001;
const FLASH_DEPTH = 1700;

const FALL_PX_PER_S = { rain: 360, sleet: 200, snow: 40 } as const;
const RAIN_COLOR = 0xaad4ff;

/**
 * Weather in the game scene (spec §6.11): rain, sleet or snow falling inside window rectangles
 * and over `outdoor` tiles, and lightning flashes for thunder. Cloud cover and fog are drawn by
 * the lighting layer.
 */
export class WeatherLayer {
  private readonly outdoor: Precipitation;
  private readonly windows: Precipitation;
  private readonly outdoorGfx: Phaser.GameObjects.Graphics;
  private readonly windowGfx: Phaser.GameObjects.Graphics;
  private readonly flash: Phaser.GameObjects.Rectangle;
  private effects: WeatherEffects = NO_WEATHER;
  private nextFlashAt = Number.POSITIVE_INFINITY;

  constructor(
    private readonly scene: Phaser.Scene,
    map: TiledMap,
  ) {
    const outdoorAreas: Area[] = shadeRuns(outdoorMask(map), map.width, map.height)
      .filter((run) => run.outdoor)
      .map((run) => ({
        x: run.x * TILE_SIZE,
        y: run.y * TILE_SIZE,
        width: run.width * TILE_SIZE,
        height: TILE_SIZE,
      }));
    const windowAreas: Area[] = getWindows(map).map((w) => ({
      x: w.x * TILE_SIZE + 2,
      y: w.y * TILE_SIZE + 2,
      width: w.width * TILE_SIZE - 4,
      height: w.height * TILE_SIZE - 4,
    }));
    this.outdoor = new Precipitation(outdoorAreas);
    this.windows = new Precipitation(windowAreas);
    this.outdoorGfx = scene.add.graphics().setDepth(OUTDOOR_DROP_DEPTH);
    this.windowGfx = scene.add.graphics().setDepth(WINDOW_DROP_DEPTH);
    this.flash = scene.add
      .rectangle(0, 0, 1, 1, 0xffffff)
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(FLASH_DEPTH)
      .setAlpha(0);
  }

  setEffects(effects: WeatherEffects): void {
    this.effects = effects;
    this.outdoor.setDensity(effects.density, TILE_SIZE);
    // Windows are small and seen up close: show a few more drops there.
    this.windows.setDensity(effects.density * 2, TILE_SIZE);
    this.nextFlashAt = effects.lightning ? this.scene.time.now + flashDelay() : Infinity;
    if (effects.particles === 'none') {
      this.outdoorGfx.clear();
      this.windowGfx.clear();
    }
  }

  /** Called every frame by the scene. */
  update(time: number, deltaMs: number): void {
    const { particles, driftPxPerS } = this.effects;
    if (particles !== 'none') {
      const fall = FALL_PX_PER_S[particles];
      // Clamp long frames (background tab) so drops do not jump.
      const dt = Math.min(deltaMs, 100);
      this.outdoor.step(dt, fall, driftPxPerS);
      this.windows.step(dt, fall, driftPxPerS);
      draw(this.outdoorGfx, this.outdoor, particles);
      draw(this.windowGfx, this.windows, particles);
    }
    if (time >= this.nextFlashAt) {
      this.nextFlashAt = time + flashDelay();
      const camera = this.scene.cameras.main;
      this.flash.setSize(camera.width, camera.height).setAlpha(0.45);
      this.scene.tweens.add({ targets: this.flash, alpha: 0, duration: 250 });
    }
  }

  destroy(): void {
    this.outdoorGfx.destroy();
    this.windowGfx.destroy();
    this.flash.destroy();
  }
}

/** 8 to 20 seconds between lightning flashes. */
const flashDelay = (): number => 8_000 + Math.random() * 12_000;

function draw(
  gfx: Phaser.GameObjects.Graphics,
  precipitation: Precipitation,
  particles: 'rain' | 'snow' | 'sleet',
): void {
  gfx.clear();
  precipitation.drops.forEach((drop, index) => {
    const area = precipitation.areas[drop.area];
    if (!area) return;
    // Never paint past the bottom of the window or outdoor strip.
    const room = area.y + area.height - Math.round(drop.y);
    const snowflake = particles === 'snow' || (particles === 'sleet' && index % 2 === 0);
    if (snowflake) {
      gfx.fillStyle(0xffffff, 0.85);
      gfx.fillRect(Math.round(drop.x), Math.round(drop.y), 2, Math.min(2, room));
    } else {
      gfx.fillStyle(RAIN_COLOR, 0.6);
      gfx.fillRect(Math.round(drop.x), Math.round(drop.y), 1, Math.min(5, room));
    }
  });
}
